import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  icon?: ReactNode;
  'data-testid'?: string;
}

/**
 * Tabs with the keyboard behaviour of the ARIA pattern: one tab stop,
 * arrows (and Home/End) move and select. Pair each tab with
 * `<TabPanel tabs={…} id={…}>`, which takes its ids from `tabsId`.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  variant = 'pill',
  tabsId,
  className,
}: {
  tabs: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  label: string;
  /** pill: rounded tabs; outlined: bordered chips; segmented: one track with the chosen tab raised; underline: page sections */
  variant?: 'pill' | 'outlined' | 'segmented' | 'underline';
  /** shared with the panels so they can name each other */
  tabsId?: string;
  className?: string;
}) {
  const generated = useId();
  const base = tabsId ?? generated;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const keys: Record<string, number> = { ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: tabs.length - 1 };
    const target = keys[event.key];
    if (target === undefined) return;
    event.preventDefault();
    const next = (target + tabs.length) % tabs.length;
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.id);
    refs.current[next]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={['ui-tabs', variant !== 'pill' ? `ui-tabs--${variant}` : '', className ?? ''].join(' ').trim()}>
      {tabs.map((tab, index) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`${base}-tab-${tab.id}`}
            aria-controls={selected ? `${base}-panel-${tab.id}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className="ui-tab"
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            data-testid={tab['data-testid']}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ tabsId, id, children, className }: { tabsId: string; id: string; children: ReactNode; className?: string }) {
  return (
    <div role="tabpanel" id={`${tabsId}-panel-${id}`} aria-labelledby={`${tabsId}-tab-${id}`} className={className}>
      {children}
    </div>
  );
}

export interface SegmentedOption<T extends string> {
  id: T;
  label: ReactNode;
  icon?: ReactNode;
  'data-testid'?: string;
}

/**
 * A choice among a few values that filters or switches a view in place
 * (Tümü / Sohbet / Kod): one track, the chosen one raised. A group of
 * buttons with aria-pressed; for panels that change, use Tabs.
 */
export function Segmented<T extends string>({ options, value, onChange, label, className }: { options: SegmentedOption<T>[]; value: T; onChange: (id: T) => void; label: string; className?: string }) {
  return (
    <div role="group" aria-label={label} className={['ui-tabs', 'ui-tabs--segmented', className ?? ''].join(' ').trim()}>
      {options.map((option) => (
        <button key={option.id} type="button" className="ui-tab" aria-pressed={option.id === value} onClick={() => onChange(option.id)} data-testid={option['data-testid']}>
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}
